import { useMutation, useQueryClient } from "@tanstack/react-query";
import { uploadPaper } from "helpers/api";
import { logUploadPaper } from "helpers/services/mixpanel";
import {
  MixpanelActionSource,
  MixpanelEvent,
} from "helpers/services/mixpanel/events";
import { useFilePicker } from "hooks/useFilePicker";
import { useCallback } from "react";

interface UseResearchPaperUploadOptions {
  onUploadTriggered?: () => void;
  onError?: (message?: string) => void;
  onSuccess?: (url?: string, fullTextURL?: string) => void;
  force_hash_paper_id?: string;
  actionSource: MixpanelActionSource;
}

interface UseResearchPaperUploadResult {
  triggerFileSelection: () => void;
  uploadFile: (file: File) => void;
  isUploading: boolean;
}

// Key for storing the uploading state in the query client cache
export const PAPER_UPLOADING_KEY = "paper-uploading-state";

// Key prefix for storing individual file upload info
export const PAPER_UPLOADING_FILE_KEY = "paper-uploading-file";

// Interface for the file info stored in react-query cache
export interface UploadingFileInfo {
  filename: string;
  size: number;
  type: string;
}

/**
 * Hook that handles research paper file selection and upload.
 * Uses the native file picker dialog and handles the upload process.
 */
export const useResearchPaperUpload = ({
  onUploadTriggered,
  onError,
  onSuccess,
  force_hash_paper_id,
  actionSource,
}: UseResearchPaperUploadOptions): UseResearchPaperUploadResult => {
  const queryClient = useQueryClient();

  const { mutate, isLoading: isUploading } = useMutation({
    mutationFn: (file: File) => {
      return uploadPaper(file, undefined, force_hash_paper_id);
    },
    onMutate: (file: File) => {
      // Set individual file uploading state
      queryClient.setQueryData([PAPER_UPLOADING_FILE_KEY, file.name], {
        filename: file.name,
        size: file.size,
        type: file.type,
      });

      // Update global uploading state (count-based)
      const currentCount =
        (queryClient.getQueryData([PAPER_UPLOADING_KEY]) as number) || 0;
      queryClient.setQueryData([PAPER_UPLOADING_KEY], currentCount + 1);

      logUploadPaper(MixpanelEvent.UploadPaperTriggered, actionSource);
      onUploadTriggered?.();
    },
    onSuccess: (response) => {
      logUploadPaper(MixpanelEvent.UploadPaperSuccess, actionSource);
      onSuccess?.(response.url, response.full_text_url);
    },
    onError: (error: any) => {
      // TODO(AAKASH): Remove this once we have a proper error handling mechanism
      if (
        error.response?.status === 500 &&
        error.response?.data?.message === "socket hang up"
      ) {
        return;
      }

      const errorMessage =
        error.response?.status === 400 && error.response.data?.message
          ? error.response.data.message
          : null;

      logUploadPaper(
        MixpanelEvent.UploadPaperError,
        actionSource,
        errorMessage
      );
      if (errorMessage) {
        onError?.(errorMessage);
      }
    },
    onSettled: (data, error, file: File) => {
      // Remove individual file info
      queryClient.removeQueries([PAPER_UPLOADING_FILE_KEY, file.name]);

      // Decrement global count
      const currentCount =
        (queryClient.getQueryData([PAPER_UPLOADING_KEY]) as number) || 0;
      queryClient.setQueryData(
        [PAPER_UPLOADING_KEY],
        Math.max(0, currentCount - 1)
      );

      queryClient.invalidateQueries({ queryKey: ["uploaded-papers"] });
    },
  });

  const handleFileSelect = useCallback(
    (files: FileList | null) => {
      const file = files?.[0];
      if (!file) return;

      mutate(file);
    },
    [mutate]
  );

  const { triggerFilePicker } = useFilePicker(handleFileSelect, {
    accept: ".pdf",
    multiple: false,
  });

  return {
    triggerFileSelection: triggerFilePicker,
    uploadFile: mutate,
    isUploading,
  };
};
